import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [], subtotal: 0, item_count: 0 });
  const [cartId, setCartId] = useState(() => {
    // For authenticated users, extract user_id from token to use as cart_id
    // For guest users, use stored cart_id from localStorage
    const token = localStorage.getItem('token');
    if (token) {
      try {
        // Decode JWT to get user_id (cart_id for authenticated users)
        const payload = JSON.parse(atob(token.split('.')[1]));
        const userId = payload.user_id;
        console.log('CartContext initializing with authenticated user, user_id:', userId);
        return userId; // Use user_id as cart_id
      } catch (error) {
        console.error('Error decoding token:', error);
        return null;
      }
    }
    const storedCartId = localStorage.getItem('cart_id');
    console.log('CartContext initializing, stored cart_id:', storedCartId);
    return storedCartId;
  });
  const [loading, setLoading] = useState(false);

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  const getHeaders = (includeCartId = true) => {
    const headers = {
      'Content-Type': 'application/json',
    };
    
    const token = localStorage.getItem('token');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
      // For authenticated users, backend uses user_id from token
      // No need to send X-Cart-Id
    } else if (includeCartId) {
      const currentCartId = cartId || localStorage.getItem('cart_id');
      if (currentCartId) {
        headers['X-Cart-Id'] = currentCartId;
      }
    }
    
    return headers;
  };

  const fetchCart = useCallback(async () => {
    const token = localStorage.getItem('token');
    const currentCartId = token ? null : (cartId || localStorage.getItem('cart_id'));
    console.log('fetchCart called, authenticated:', !!token, 'cart_id:', currentCartId);
    
    try {
      setLoading(true);
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        // For authenticated users, backend will use user_id from token
      } else if (currentCartId) {
        headers['X-Cart-Id'] = currentCartId;
      }
      
      const response = await fetch(`${API_BASE}/api/cart`, {
        headers
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Cart data fetched:', data);
        setCart(data);
        console.log('Cart state updated via setCart, should trigger ResponsiveHeader useEffect');
        
        // Set cartId from response for both guest and authenticated users
        if (data.id) {
          if (!cartId || cartId !== data.id) {
            console.log('Setting cart_id from fetch:', data.id);
            setCartId(data.id);
          }
          
          // Store cart_id in localStorage only for guest users
          if (!token) {
            localStorage.setItem('cart_id', data.id);
          }
        }
      } else {
        console.error('Failed to fetch cart, status:', response.status);
      }
    } catch (error) {
      console.error('Error fetching cart:', error);
    } finally {
      setLoading(false);
    }
  }, [API_BASE, cartId]);

  // Load cart on mount
  useEffect(() => {
    console.log('CartContext mounted, calling fetchCart');
    fetchCart();
  }, [fetchCart]);

  const addToCart = async (productId, variantId = null, quantity = 1) => {
    console.log('CartContext.addToCart called with:', { productId, variantId, quantity });
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/cart/add`, {
        method: 'POST',
        headers: getHeaders(true),
        body: JSON.stringify({
          product_id: productId,
          variant_id: variantId,
          quantity
        })
      });

      console.log('API Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('API Response data:', data);
        
        // Store cart_id if it's a new cart
        if (data.cart_id) {
          console.log('Storing cart_id from addToCart:', data.cart_id);
          setCartId(data.cart_id);
          localStorage.setItem('cart_id', data.cart_id);
        }
        
        // Refresh cart
        console.log('Fetching updated cart...');
        await fetchCart();
        return { success: true };
      } else {
        const error = await response.json();
        console.error('API Error:', error);
        return { success: false, error: error.detail };
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const updateCartItem = async (itemId, quantity) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/cart/update/${itemId}`, {
        method: 'PUT',
        headers: getHeaders(true),
        body: JSON.stringify({ quantity })
      });

      if (response.ok) {
        await fetchCart();
        return { success: true };
      } else {
        const error = await response.json();
        return { success: false, error: error.detail };
      }
    } catch (error) {
      console.error('Error updating cart:', error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (itemId) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/cart/remove/${itemId}`, {
        method: 'DELETE',
        headers: getHeaders(true)
      });

      if (response.ok) {
        await fetchCart();
        return { success: true };
      } else {
        const error = await response.json();
        return { success: false, error: error.detail };
      }
    } catch (error) {
      console.error('Error removing from cart:', error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const mergeCart = async () => {
    const guestCartId = localStorage.getItem('cart_id');
    if (!guestCartId) return;

    try {
      const response = await fetch(`${API_BASE}/api/cart/merge`, {
        method: 'POST',
        headers: getHeaders(false),
        body: JSON.stringify({ guest_cart_id: guestCartId })
      });

      if (response.ok) {
        // Clear guest cart ID
        localStorage.removeItem('cart_id');
        setCartId(null);
        
        // Fetch merged cart
        await fetchCart();
      }
    } catch (error) {
      console.error('Error merging cart:', error);
    }
  };

  const clearCart = () => {
    setCart({ items: [], subtotal: 0, item_count: 0 });
    setCartId(null);
    localStorage.removeItem('cart_id');
  };

  const value = {
    cart,
    cartId,
    loading,
    addToCart,
    updateCartItem,
    removeFromCart,
    fetchCart,
    mergeCart,
    clearCart
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
